import { useEffect, useState } from "react";
import api, { formatApiError, authedMedia } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Award, Loader2 } from "lucide-react";

// Admin editor: one ACE representative per specialty (Falls, Dementia, …).
export function AceExperts() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/ace-experts", { params: { include_inactive: true } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []);

  const remove = async (r) => {
    try { await api.delete(`/ace-experts/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (r, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/ace-experts/${r.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo saved for ${r.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card title="ACE experts by specialty" data-testid="ace-experts-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <p className="text-sm text-zinc-500 flex-1 min-w-[16rem]">
          The ACE representative for each specialty. Clinical Instructors and DORs see this list on their dashboard with a message button.
        </p>
        <Button size="sm" onClick={() => setEditing({})} className="bg-cc-blue text-white" data-testid="new-ace-btn"><Plus className="w-4 h-4 mr-1" />Add specialty</Button>
      </div>
      {!rows ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cc-blue" /></div>
        : rows.length === 0 ? <EmptyState text="No ACE experts yet — add Falls, Dementia and the rest." /> : (
          <div className="space-y-2" data-testid="ace-list">
            {rows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 border rounded-md px-3 py-2.5" data-testid={`ace-row-${r.id}`}>
                <Award className="w-4 h-4 text-cc-orange shrink-0" />
                <Avatar src={r.has_photo ? authedMedia(`/ace-experts/${r.id}/photo`) : null} name={r.name} size={34} testId={`ace-avatar-${r.id}`} />
                <div className="min-w-[8rem]">
                  <p className="text-sm font-semibold text-zinc-900">{r.specialty}</p>
                  <p className="text-xs text-zinc-500">{r.name}{r.title ? ` · ${r.title}` : ""}</p>
                </div>
                <div className="text-xs text-zinc-500 flex-1 min-w-[12rem]">
                  {r.email || "no email"}{r.phone ? ` · ${r.phone}` : ""}
                  {r.notes && <div className="text-zinc-400 mt-0.5">{r.notes}</div>}
                </div>
                <div className="flex items-center gap-3 ml-auto">
                  <label className="text-xs text-cc-blue hover:underline cursor-pointer" data-testid={`ace-photo-label-${r.id}`}>
                    {r.has_photo ? "Change photo" : "Add photo"}
                    <input type="file" accept="image/*" className="hidden" data-testid={`ace-photo-${r.id}`}
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(r, e.target.files[0])} />
                  </label>
                  <button onClick={() => setEditing(r)} className="text-zinc-400 hover:text-cc-blue" data-testid={`ace-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600" data-testid={`ace-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      {editing && <AceDialog expert={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Card>
  );
}

function AceDialog({ expert, onClose, onSaved }) {
  const [form, setForm] = useState({ ...expert });
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  // Server-side lookup against the whole company directory (debounced).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setMatches([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/company-directory", { params: { q: term } })
        .then(({ data }) => setMatches(data.people || []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { clearTimeout(t); setSearching(false); };
  }, [q]);
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch(`/ace-experts/${form.id}`, form);
      else await api.post("/ace-experts", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ace-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit ACE expert" : "Add ACE expert"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Specialty">
            <Input value={form.specialty || ""} onChange={(e) => setForm({ ...form, specialty: e.target.value })}
              placeholder="Falls · Dementia · Wound care" data-testid="ace-specialty" />
          </Field>
          <Field label="Find them in the company directory (optional)">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search anyone by name or email…" data-testid="ace-search" />
            {q.trim().length === 1 && <p className="text-xs text-zinc-400 mt-1">Keep typing — at least two letters.</p>}
            {searching && <p className="text-xs text-zinc-400 mt-1" data-testid="ace-searching">Searching the company directory…</p>}
            {!searching && q.trim().length > 1 && matches.length === 0 && (
              <p className="text-xs text-zinc-400 mt-1" data-testid="ace-no-matches">Nobody found — type their name and email below instead.</p>
            )}
            {matches.length > 0 && (
              <div className="border rounded-md mt-1.5 divide-y" data-testid="ace-search-results">
                {matches.map((p) => (
                  <button key={p.email} type="button"
                    onClick={() => { setForm({ ...form, name: p.name, email: p.email, notes: form.notes || p.title || "" }); setQ(""); setMatches([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50" data-testid={`ace-pick-${p.email}`}>
                    <span className="font-medium text-zinc-800">{p.name}</span> <span className="text-xs text-zinc-400">{p.email}</span>
                    {p.title && <span className="block text-xs text-zinc-400">{p.title}{p.office ? ` · ${p.office}` : ""}</span>}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ace-name" /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ace-email" /></Field>
            <Field label="Phone (optional)"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ace-phone" /></Field>
          </div>
          <Field label="Title / note (optional)">
            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ask about balance screens and falls programs" data-testid="ace-notes" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="ace-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Read-only card for Clinical Instructors / DORs.
export function AceExpertList({ experts }) {
  if (!experts || experts.length === 0) return null;
  return (
    <Card title="ACE experts" data-testid="ace-expert-list">
      <p className="text-sm text-zinc-500 mb-3">Each specialty has an ACE representative. Email them directly with a clinical question.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {experts.map((e) => (
          <div key={e.id} className="border rounded-lg px-3.5 py-3 hover:border-cc-orange transition-colors" data-testid={`ace-expert-${e.id}`}>
            <div className="flex items-start gap-3">
              <Avatar src={e.has_photo ? authedMedia(`/ace-experts/${e.id}/photo`) : null} name={e.name} size={44} testId={`ace-expert-avatar-${e.id}`} />
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-cc-orange">{e.specialty}</p>
                <p className="text-sm font-semibold text-zinc-900 mt-0.5">{e.name}</p>
                {e.notes && <p className="text-xs text-zinc-500 mt-0.5">{e.notes}</p>}
                {e.email && (
                  <a href={`mailto:${e.email}`} className="text-xs text-cc-blue hover:underline mt-1.5 inline-block break-all" data-testid={`ace-email-${e.id}`}>{e.email}</a>
                )}
                {e.phone && <p className="text-xs text-zinc-400">{e.phone}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
